import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect } from 'react';

import { useGetInstancesQuery } from '@api/connectInstance';
import { RdConnectInstance, RdConnectInstancePingResponse } from '@apiContract';
import useLocalStorage from '@hooks/useLocalStorage';
import isUndefined from 'lodash/fp/isUndefined';

import { RdConnectPingListParams } from '@src/api/automatedCapture';

import { AutomatedCameraLocalStorageKeys } from '../../types';

type Props = {
  loading: boolean;
  success: boolean;
  makeRequest: (
    params?: RdConnectPingListParams,
    retryOptions?: {
      attemptLimit?: number;
    },
  ) => Promise<RdConnectInstancePingResponse>;
  rdConnectInstanceId: number;
  setUserDevice: Dispatch<SetStateAction<RdConnectInstance>>;
  onChooseCamera: VoidFunction;
};

const CameraStatus: FC<Props> = ({
  loading,
  success,
  makeRequest,
  rdConnectInstanceId,
  setUserDevice,
  onChooseCamera,
}) => {
  const [storedDeviceId, setStoredDeviceId] = useLocalStorage<number | undefined>(
    AutomatedCameraLocalStorageKeys.UserDeviceId,
    undefined,
  );

  const { data: devices = [], isLoading } = useGetInstancesQuery();

  useEffect(() => {
    if (devices.length > 0) {
      const matchedDevice = !isUndefined(storedDeviceId)
        ? devices.find((device) => device.id === storedDeviceId) || devices[0]
        : devices[0];

      setStoredDeviceId(matchedDevice.id);
      setUserDevice(matchedDevice);
      makeRequest({ rdConnectInstanceId: matchedDevice.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, isLoading]);

  const onRetryClick = () => {
    void makeRequest({ rdConnectInstanceId });
  };

  const cameraStatusView = success ? (
    <>
      <FiberManualRecordIcon color="success" sx={{ mr: 1 }} />
      <Typography variant="body2">Camera connected</Typography>
    </>
  ) : (
    <>
      <FiberManualRecordIcon color="error" sx={{ mr: 1 }} />
      <Typography variant="body2" mr={2}>
        Camera failed to connect
      </Typography>
      <Button size="small" variant="soft" color="inherit" startIcon={<ReplayIcon />} onClick={onRetryClick}>
        Retry
      </Button>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      {loading ? (
        <>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Connecting to camera...</Typography>
        </>
      ) : (
        cameraStatusView
      )}
      {success && (
        <Tooltip arrow title="Change Camera">
          <IconButton sx={{ ml: 2 }} onClick={() => onChooseCamera()}>
            <CameraswitchIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default CameraStatus;
